import { ReactComponent as ShowClaim } from 'assett/icons/show_password.svg';
import SelectGeneric from 'components/commons/form/SelectGeneric';
import UploadGrid from 'components/UploadFile/UploadGrid/UploadGrid';
import dayjs from 'dayjs';
import { getClaimDetails } from 'feature/claims/claimSlice';
import { getFraudStatuses } from 'feature/form/formSlice';
import {
	getFraudTrackingDetail,
	resetFraudDetail,
	saveFraud,
	setFraudValue,
} from 'feature/fraudTracking/fraudTrackingSlice';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import { Button, DatePicker, Grid, Stack, TextField, Typography } from 'styles';
import { theme } from 'styles/theme/ThemeStyle';
import { IFraudTrackingDetail } from 'types/FraudTracking';
import { STATE_KEYS } from 'types/Store';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import { dateZuluToDayjs, formatDayjsForBE, getShortDate } from 'utils/utils';
import FraudAttachmentsDetails from './FraudAttachmentsDetails';

const FraudDetailsField = ({
	label,
	value,
	required = false,
}: {
	label: string;
	value: string;
	required?: boolean;
}) => {
	return (
		<TextField
			InputProps={{ readOnly: true, disableUnderline: true }}
			label={label}
			sx={{
				width: '100%',
				'& label.Mui-focused': {
					color: theme => theme.palette.grey2.main,
				},
				'.MuiInputBase-root': {
					border: '1px solid transparent !important',
				},
			}}
			value={value}
			required={required}
		></TextField>
	);
};

const FraudErrorMessage = ({ errorMessage }: { errorMessage?: string }) => {
	return (
		<Typography
			variant="caption"
			color={theme.palette.error.main}
			position={'absolute'}
			bottom={-23}
			left={13}
		>
			{errorMessage || 'This field is mandatory'}
		</Typography>
	);
};

const formSelectStyle = {
	'& .MuiTypography-root': {
		display: 'none',
	},
	'& .MuiInputLabel-root': {
		color: theme.palette.primary.main,
	},
};

const FraudTrackingDetail = ({
	isFraudDetails,
}: {
	isFraudDetails: boolean;
}) => {
	const { fraudId, claimUUID } = useParams<{
		fraudId: string;
		claimUUID: string;
	}>();

	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();

	// const { claimData: claim } = useSelector((store: RootState) => store.claim);
	const { fraudDetail, triggerRefresh } = useSelector(
		(store: RootState) => store.fraudTracking,
	);
	const { fraudStatuses } = useSelector((store: RootState) => store.form);
	const { claimData: claim } = useSelector((store: RootState) => store.claim);

	const [isEditing, setIsEditing] = useState(!isFraudDetails);
	const [errors, setErrors] = useState<string[]>([]);
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.DB_LABELS,
	});

	useEffect(() => {
		if (fraudId) {
			dispatch(getFraudTrackingDetail({ id: fraudId }));
			setIsEditing(false);
		} else if (triggerRefresh && fraudDetail?.fraudId) {
			dispatch(
				getFraudTrackingDetail({
					id: String(fraudDetail.fraudId),
				}),
			);
			setIsEditing(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, fraudId, triggerRefresh]);

	useEffect(() => {
		if (!fraudId && claimUUID) {
			dispatch(resetFraudDetail(claimUUID));
		}
		return () => {
			dispatch(resetFraudDetail(''));
		};
	}, [dispatch, fraudId, claimUUID]);

	useEffect(() => {
		if (claimUUID) {
			dispatch(getClaimDetails(claimUUID));
		} else if (fraudDetail?.claimUUID) {
			dispatch(getClaimDetails(fraudDetail?.claimUUID));
		}
	}, [dispatch, claimUUID, fraudDetail?.claimUUID]);

	useEffect(() => {
		dispatch(getFraudStatuses(''));
	}, [dispatch]);

	const handleValueChange = (
		inputName: keyof IFraudTrackingDetail,
		payload: string,
	) => {
		dispatch(
			setFraudValue({
				name: inputName,
				value: payload !== 'All' ? payload : '',
			}),
		);
	};

	const requiredFields = {
		fraudId: false,
		claimUUID: true,
		detectionDate: true,
		investigationStatus: true,
		outcomeSummary: false,
		referralDate: false,
		internalNotes: false,
		createdBy: false,
		attachments: false,
	};

	const validateForm = () => {
		let errors = [];

		if (requiredFields.detectionDate && fraudDetail?.detectionDate === '') {
			errors.push('detectionDate');
		}

		if (
			requiredFields.investigationStatus &&
			fraudDetail?.investigationStatus === ''
		) {
			errors.push('investigationStatus');
		}

		setErrors(errors);

		return errors.length;
	};

	const handleDiscardChanges = () => {
		if (fraudDetail?.fraudId) {
			dispatch(
				getFraudTrackingDetail({
					id: String(fraudDetail.fraudId),
				}),
			);
		}
		setIsEditing(false);
	};

	return (
		<Stack gap={5} height={'100%'} direction={'column'} position={'relative'}>
			<Stack flexDirection={'row'} justifyContent={'flex-end'}>
				{isFraudDetails && (
					<Button
						startIcon={<ShowClaim />}
						variant="text"
						onClick={() => navigate(`/view-claims/${fraudDetail?.claimUUID || ''}`)}
						sx={{
							mr: 0,
							width: 'fit-content',
							p: 0,
							textDecoration: 'underline',
							gap: '8px',
							'&:hover': {
								textDecoration: 'underline',
							},
						}}
					>
						{tClientLabels('fraud.viewClaim')}
					</Button>
				)}
			</Stack>
			<Stack gap={7.5}>
				{!isEditing ? (
					<>
						<Grid container columnSpacing={2}>
							<Grid item sm={3}>
								<FraudDetailsField
									label={tClientLabels('forms.header.detectionDate')}
									value={
										fraudDetail?.detectionDate
											? getShortDate(fraudDetail?.detectionDate)
											: '-'
									}
									required={requiredFields.detectionDate}
								/>
							</Grid>
							<Grid item sm={3}>
								<FraudDetailsField
									label={tClientLabels('forms.header.investigationStatus')}
									value={
										fraudDetail?.investigationStatus
											? tDbLabels(
													`fraud_status.${fraudDetail?.investigationStatus?.toLowerCase()}`,
												)
											: '-'
									}
									required={requiredFields.investigationStatus}
								/>
							</Grid>
							<Grid item sm={6}>
								<FraudDetailsField
									label={tClientLabels('forms.header.outcomeSummary')}
									value={fraudDetail?.outcomeSummary || '-'}
									required={requiredFields.outcomeSummary}
								/>
							</Grid>
						</Grid>
						<Grid container columnSpacing={2}>
							<Grid item sm={3}>
								<FraudDetailsField
									label={tClientLabels('forms.header.referralDate')}
									value={
										fraudDetail?.referralDate
											? getShortDate(fraudDetail?.referralDate)
											: '-'
									}
									required={requiredFields.referralDate}
								/>
							</Grid>
							<Grid item sm={6}>
								<FraudDetailsField
									label={tClientLabels('forms.header.internalNotes')}
									value={fraudDetail?.internalNotes || '-'}
									required={requiredFields.internalNotes}
								/>
							</Grid>
						</Grid>
						<Grid gap={2}>
							<FraudAttachmentsDetails />
						</Grid>
					</>
				) : (
					<>
						<Grid container columnSpacing={2}>
							<Grid item sm={3}>
								<Stack
									width={'100%'}
									position={'relative'}
									sx={
										errors.includes('detectionDate')
											? {
													'& .MuiInputBase-root': {
														borderColor: theme => theme.palette.error.main,
													},
												}
											: {}
									}
								>
									<DatePicker
										readonly
										name="detectionDate"
										variant="field"
										label={tClientLabels('forms.header.detectionDate')}
										value={
											fraudDetail?.detectionDate ? dayjs(fraudDetail?.detectionDate) : null
										}
										onChange={value =>
											handleValueChange('detectionDate', formatDayjsForBE(value))
										}
										required={requiredFields.detectionDate}
										minDate={claim?.claimDate ? dayjs(claim?.claimDate) : undefined}
									/>
									{errors.includes('detectionDate') && <FraudErrorMessage />}
								</Stack>
							</Grid>
							<Grid item sm={3}>
								<Stack width={'100%'} position={'relative'}>
									<SelectGeneric
										action={payload => handleValueChange('investigationStatus', payload)}
										items={[
											...fraudStatuses.map((el, index) => {
												return {
													value: tDbLabels(el.code),
													text: tDbLabels(`fraud_status.${el.code.toLowerCase()}`),
													id: index,
												};
											}),
										]}
										name={'investigationStatus'}
										sx={{ width: '100%' }}
										sxForm={formSelectStyle}
										initialValue={
											fraudDetail?.investigationStatus
												? tDbLabels(fraudDetail?.investigationStatus)
												: undefined
										}
										error={errors.includes('investigationStatus')}
										required={requiredFields.investigationStatus}
									/>
									{errors.includes('investigationStatus') && <FraudErrorMessage />}
								</Stack>
							</Grid>
							<Grid item sm={6}>
								<TextField
									label={tClientLabels('forms.header.outcomeSummary')}
									sx={{ width: '100%' }}
									defaultValue={fraudDetail?.outcomeSummary || ''}
									onChange={event =>
										handleValueChange('outcomeSummary', event.target.value)
									}
									required={requiredFields.outcomeSummary}
								></TextField>
							</Grid>
						</Grid>
						<Grid container columnSpacing={2}>
							<Grid item sm={3}>
								<DatePicker
									readonly
									name="referralDate"
									variant="field"
									label={tClientLabels('forms.header.referralDate')}
									value={
										fraudDetail?.referralDate
											? dayjs(dateZuluToDayjs(fraudDetail?.referralDate))
											: null
									}
									onChange={value =>
										handleValueChange('referralDate', formatDayjsForBE(value))
									}
									required={requiredFields.referralDate}
								/>
							</Grid>
							<Grid item sm={6}>
								<TextField
									label={tClientLabels('forms.header.internalNotes')}
									sx={{ width: '100%' }}
									defaultValue={fraudDetail?.internalNotes || ''}
									onChange={event =>
										handleValueChange('internalNotes', event.target.value)
									}
									required={requiredFields.internalNotes}
								/>
							</Grid>
						</Grid>
						<UploadGrid
							documentType="fraud"
							data={fraudDetail?.attachments}
							clearDocumentsKey={STATE_KEYS.FRAUD}
						/>
					</>
				)}
			</Stack>
			<Stack
				direction={'row'}
				position={'absolute'}
				right={0}
				bottom={'20px'}
				sx={{
					gap: '20px',
					alignContent: 'center',
					justifyContent: 'flex-end',
				}}
			>
				{isEditing ? (
					<>
						{isFraudDetails ? (
							<Button
								type="button"
								variant="outlined"
								fullWidth={false}
								onClick={handleDiscardChanges}
							>
								{tClientLabels('commons.buttons.back')}
							</Button>
						) : (
							<Button
								type="button"
								variant="outlined"
								fullWidth={false}
								onClick={() => navigate(`/view-claims/${fraudDetail?.claimUUID}`)}
							>
								{tClientLabels('commons.buttons.cancel')}
							</Button>
						)}
						<Button
							type="button"
							variant="contained"
							fullWidth={false}
							onClick={() => {
								if (validateForm() === 0) {
									dispatch(saveFraud({})).then(() => {
										if (isFraudDetails) {
											setIsEditing(false);
										} else {
											navigate('/view-fraud-tracking');
										}
									});
								}
							}}
						>
							{tClientLabels('commons.buttons.save')}
						</Button>
					</>
				) : fraudDetail?.investigationStatus !== 'CLOSED' ? (
					<Button
						type="button"
						variant="contained"
						// disabled={selectedClaims.length > 0 ? false : true}
						fullWidth={false}
						onClick={() => setIsEditing(true)}
					>
						{tClientLabels('commons.buttons.edit')}
					</Button>
				) : null}
			</Stack>
		</Stack>
	);
};

export default FraudTrackingDetail;
